import { api, throwApiError } from "../../../api/api";
import type { DpLeaveType } from "./leavesApi";
import type {
  DpAllowanceBase,
  DpBankAccountType,
  DpDependentRelationship,
  DpEmployeeStatus,
  DpGender,
  DpMaritalStatus,
  DpPaginated,
  SalarioFamiliaReason,
} from "./dpTypes";
import type { DpEmployeeContract } from "./contractsApi";

export type DpEmployeeListItem = {
  id: string;
  fullName: string;
  socialName?: string | null;
  cpf: string | null;
  status: DpEmployeeStatus;
  email?: string | null;
  phone?: string | null;
  createdAt: string;
  /** Loja da versão de contrato vigente hoje (null = sem loja ou sem contrato ativo). */
  store?: { id: string; nomeFantasia: string } | null;
};

export type DpEmployeeDependent = {
  id: string;
  employeeId: string;
  fullName: string;
  cpf?: string | null;
  birthDate: string;
  relationship: DpDependentRelationship;
  isIrDependent: boolean;
  isSalarioFamiliaEligible: boolean;
  hasDisability: boolean;
  isUniversityStudent: boolean;
  notes?: string | null;
};

export type DpEmployeeDetail = {
  id: string;
  fullName: string;
  socialName?: string | null;
  cpf: string | null;
  birthDate: string | null;
  gender: DpGender;
  maritalStatus?: DpMaritalStatus | null;
  nationality?: string | null;
  birthCity?: string | null;
  birthState?: string | null;
  motherName?: string | null;
  fatherName?: string | null;
  rg?: string | null;
  rgIssuer?: string | null;
  rgIssuedAt?: string | null;
  ctpsNumber?: string | null;
  ctpsSeries?: string | null;
  ctpsState?: string | null;
  pisNis?: string | null;
  voterTitle?: string | null;
  militaryCert?: string | null;
  cnh?: string | null;
  cnhCategory?: string | null;
  cnhExpiresAt?: string | null;
  email?: string | null;
  phone?: string | null;
  addressStreet?: string | null;
  addressNumber?: string | null;
  addressComplement?: string | null;
  addressDistrict?: string | null;
  addressCity?: string | null;
  addressState?: string | null;
  addressZip?: string | null;
  bankCode?: string | null;
  bankName?: string | null;
  bankBranch?: string | null;
  bankAccount?: string | null;
  bankAccountType?: DpBankAccountType | null;
  pixKey?: string | null;
  emergencyContactName?: string | null;
  emergencyContactPhone?: string | null;
  emergencyContactRelationship?: string | null;
  status: DpEmployeeStatus;
  notes?: string | null;
  /** Foto: só serve de chave de cache — a imagem vem de GET /dp/employees/:id/photo. */
  photoAssetId?: string | null;
  userId?: string | null;
  user?: { id: string; nomeCompleto: string; cpf: string; userType: string } | null;
  dependents: DpEmployeeDependent[];
  contracts: DpEmployeeContract[];
  /** Plano D: afastamento que cobre hoje (sem dado de saúde) e próximas férias não canceladas. */
  activeLeave?: { id: string; type: DpLeaveType; startDate: string; endDate: string | null } | null;
  nextVacation?: { id: string; startDate: string; endDate: string; days: number; status: string } | null;
};

/**
 * Corpo aceito no cadastro/edição do colaborador.
 *
 * Espelha `CreateEmployeeDto` do backend campo a campo: lá o `ValidationPipe` roda
 * com `whitelist: true`, então qualquer chave que não exista no DTO é descartada
 * SEM erro — o sintoma seria "o campo não salva e ninguém avisa".
 */
export type UpsertEmployeePayload = {
  fullName: string;
  socialName?: string;
  cpf: string;
  birthDate: string;
  gender?: DpGender;
  maritalStatus?: DpMaritalStatus;
  nationality?: string;
  birthCity?: string;
  birthState?: string;
  motherName?: string;
  fatherName?: string;
  rg?: string;
  rgIssuer?: string;
  rgIssuedAt?: string;
  ctpsNumber?: string;
  ctpsSeries?: string;
  ctpsState?: string;
  pisNis?: string;
  voterTitle?: string;
  militaryCert?: string;
  cnh?: string;
  cnhCategory?: string;
  cnhExpiresAt?: string;
  email?: string;
  phone?: string;
  addressStreet?: string;
  addressNumber?: string;
  addressComplement?: string;
  addressDistrict?: string;
  addressCity?: string;
  addressState?: string;
  addressZip?: string;
  bankCode?: string;
  bankName?: string;
  bankBranch?: string;
  bankAccount?: string;
  bankAccountType?: DpBankAccountType;
  pixKey?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  emergencyContactRelationship?: string;
  notes?: string;
};

export type UpsertDependentPayload = {
  fullName: string;
  cpf?: string;
  birthDate: string;
  relationship: DpDependentRelationship;
  isIrDependent?: boolean;
  isSalarioFamiliaEligible?: boolean;
  hasDisability?: boolean;
  isUniversityStudent?: boolean;
  notes?: string;
};

/** Criação = perfil + filhos/dependentes, gravados juntos (tudo ou nada). */
export type CreateEmployeePayload = UpsertEmployeePayload & {
  dependents?: UpsertDependentPayload[];
};

type AllowanceSummary = {
  percent: string;
  base: DpAllowanceBase | null;
  baseValue: string | null;
  amount: string | null;
};

/**
 * Remuneração de referência (GET /dp/employees/:id/remuneration-summary).
 * Calculada na leitura, nada é gravado. Valores em reais chegam como string "0.00".
 */
export type DpRemunerationSummary = {
  at: string;
  contract: {
    versionId: string;
    benefitElectionId: string | null;
    baseSalaryMonthly: string;
    insalubrity: AllowanceSummary | null;
    hazard: AllowanceSummary | null;
    complementaryActivity: AllowanceSummary | null;
    referenceRemuneration: string | null;
    salaryAdvance: { percent: string; amount: string } | null;
    transportVoucher: { dailyAmount: string; employeePct: string | null } | null;
    mealVoucher: { dailyAmount: string; employeePct: string | null } | null;
  } | null;
  salarioFamilia: {
    ceiling: string | null;
    quota: string | null;
    nf: number;
    quotaTotal: string | null;
    dependents: Array<{
      dependentId: string;
      eligible: boolean | null;
      reasons: SalarioFamiliaReason[];
      divergent: boolean;
    }>;
  };
  nd: number;
  warnings: string[];
};

export type ListEmployeesParams = {
  page?: number;
  limit?: number;
  q?: string;
  status?: DpEmployeeStatus;
  costCenterId?: string;
  employerId?: string;
  /** Loja da versão de contrato vigente hoje. */
  storeId?: string;
  /** "true": só quem tem contrato ativo e não está lotado em nenhuma loja. */
  withoutStore?: "true";
};

const auth = (token: string) => {
  const raw = token?.startsWith("Bearer ") ? token.slice(7) : token;
  return { headers: { Authorization: `Bearer ${raw}` } };
};

export const listEmployeesApi = async (
  token: string,
  params: ListEmployeesParams = {}
): Promise<DpPaginated<DpEmployeeListItem>> => {
  try {
    const { data } = await api.get<DpPaginated<DpEmployeeListItem>>("/dp/employees", {
      ...auth(token),
      params,
    });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao listar colaboradores.");
    throw error;
  }
};

export const getEmployeeApi = async (
  token: string,
  id: string
): Promise<DpEmployeeDetail> => {
  try {
    const { data } = await api.get<DpEmployeeDetail>(`/dp/employees/${id}`, auth(token));
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao carregar o colaborador.");
    throw error;
  }
};

export const createEmployeeApi = async (
  token: string,
  payload: CreateEmployeePayload
): Promise<DpEmployeeDetail> => {
  try {
    const { data } = await api.post<DpEmployeeDetail>("/dp/employees", payload, auth(token));
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao cadastrar colaborador.");
    throw error;
  }
};

export const updateEmployeeApi = async (
  token: string,
  id: string,
  payload: Partial<UpsertEmployeePayload>
): Promise<DpEmployeeDetail> => {
  try {
    const { data } = await api.patch<DpEmployeeDetail>(
      `/dp/employees/${id}`,
      payload,
      auth(token)
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao salvar o colaborador.");
    throw error;
  }
};

export const deleteEmployeeApi = async (token: string, id: string): Promise<void> => {
  try {
    await api.delete(`/dp/employees/${id}`, auth(token));
  } catch (error) {
    throwApiError(error, "Erro ao excluir colaborador.");
    throw error;
  }
};

export const getUserSuggestionApi = async (
  token: string,
  id: string
): Promise<{ user: { id: string; nomeCompleto: string; cpf: string; userType: string } | null; alreadyLinked: boolean }> => {
  try {
    const { data } = await api.get(`/dp/employees/${id}/user-suggestion`, auth(token));
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao buscar conta de usuário correspondente.");
    throw error;
  }
};

export const linkEmployeeUserApi = async (
  token: string,
  id: string,
  userId: string
): Promise<DpEmployeeDetail> => {
  try {
    const { data } = await api.post<DpEmployeeDetail>(
      `/dp/employees/${id}/link-user`,
      { userId },
      auth(token)
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao vincular a conta de usuário.");
    throw error;
  }
};

export const unlinkEmployeeUserApi = async (
  token: string,
  id: string
): Promise<DpEmployeeDetail> => {
  try {
    const { data } = await api.delete<DpEmployeeDetail>(
      `/dp/employees/${id}/link-user`,
      auth(token)
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao desfazer o vínculo.");
    throw error;
  }
};

// ── Dependentes ─────────────────────────────────────────────────────────────

export const createDependentApi = async (
  token: string,
  employeeId: string,
  payload: UpsertDependentPayload
): Promise<DpEmployeeDependent> => {
  try {
    const { data } = await api.post<DpEmployeeDependent>(
      `/dp/employees/${employeeId}/dependents`,
      payload,
      auth(token)
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao cadastrar dependente.");
    throw error;
  }
};

export const createDependentsBulkApi = async (
  token: string,
  employeeId: string,
  dependents: UpsertDependentPayload[]
): Promise<DpEmployeeDependent[]> => {
  try {
    const { data } = await api.post<DpEmployeeDependent[]>(
      `/dp/employees/${employeeId}/dependents/bulk`,
      { dependents },
      auth(token)
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao cadastrar os dependentes.");
    throw error;
  }
};

export const updateDependentApi = async (
  token: string,
  employeeId: string,
  dependentId: string,
  payload: Partial<UpsertDependentPayload>
): Promise<DpEmployeeDependent> => {
  try {
    const { data } = await api.patch<DpEmployeeDependent>(
      `/dp/employees/${employeeId}/dependents/${dependentId}`,
      payload,
      auth(token)
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao editar dependente.");
    throw error;
  }
};

export const deleteDependentApi = async (
  token: string,
  employeeId: string,
  dependentId: string
): Promise<void> => {
  try {
    await api.delete(`/dp/employees/${employeeId}/dependents/${dependentId}`, auth(token));
  } catch (error) {
    throwApiError(error, "Erro ao excluir dependente.");
    throw error;
  }
};

// ── Remuneração de referência ───────────────────────────────────────────────

export const getRemunerationSummaryApi = async (
  token: string,
  employeeId: string,
  at?: string
): Promise<DpRemunerationSummary> => {
  try {
    const { data } = await api.get<DpRemunerationSummary>(
      `/dp/employees/${employeeId}/remuneration-summary`,
      { ...auth(token), params: at ? { at } : undefined }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao calcular a remuneração de referência.");
    throw error;
  }
};

// ── Foto ────────────────────────────────────────────────────────────────────

/** Tipos e tamanho aceitos — espelham o módulo uploads do backend. */
export const EMPLOYEE_PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"];
export const EMPLOYEE_PHOTO_MAX_BYTES = 10 * 1024 * 1024;

export const uploadEmployeePhotoApi = async (
  token: string,
  employeeId: string,
  file: File
): Promise<{ photoAssetId: string }> => {
  const form = new FormData();
  form.append("file", file);
  try {
    const { data } = await api.post<{ photoAssetId: string }>(
      `/dp/employees/${employeeId}/photo`,
      form,
      { headers: { ...auth(token).headers, "Content-Type": "multipart/form-data" } }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao enviar a foto.");
    throw error;
  }
};

/**
 * A foto vem como blob pela rota do DP (com o token do staff) — um <img src>
 * direto não mandaria o Authorization. Quem chama cria e revoga o object URL.
 */
export const getEmployeePhotoBlobApi = async (token: string, employeeId: string): Promise<Blob> => {
  try {
    const { data } = await api.get<Blob>(`/dp/employees/${employeeId}/photo`, {
      ...auth(token),
      responseType: "blob",
    });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao carregar a foto.");
    throw error;
  }
};

export const deleteEmployeePhotoApi = async (token: string, employeeId: string): Promise<void> => {
  try {
    await api.delete(`/dp/employees/${employeeId}/photo`, auth(token));
  } catch (error) {
    throwApiError(error, "Erro ao remover a foto.");
    throw error;
  }
};
